import React from 'react';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function TimelineView({ container }) {
    // Define standard milestones
    const milestones = [
        { key: 'booked', label: 'Booked', date: container.createdAt }, // Fallback to createdAt or similar
        { key: 'wip', label: 'WIP', date: null },
        { key: 'dispatched', label: 'Dispatched', date: container.dpi },
        { key: 'in transit', label: 'In Transit', date: container.departureDate },
        { key: 'arrived', label: 'Arrived', date: container.eta },
        { key: 'delivered', label: 'Delivered', date: container.deliveryDate },
    ];

    // Helper to safely parse dates
    const safeDate = (d) => {
        if (!d) return null;
        if (d.toDate) return d.toDate(); // Firebase Timestamp
        if (typeof d === 'string') return parseISO(d);
        return new Date(d);
    };

    // Determine current status index
    const status = (container.status || 'booked').toLowerCase();
    const currentIndex = milestones.findIndex(m => m.key === status) || 0;

    return (
        <div className="p-6 bg-slate-50 border-t border-slate-200">
            <h4 className="text-sm font-bold text-navy-900 mb-6 uppercase tracking-wider">Shipment Progress</h4>

            <div className="relative flex items-center justify-between w-full max-w-4xl mx-auto">
                {/* Progress Bar Background */}
                <div className="absolute top-4 left-0 w-full h-1 bg-slate-200 -z-10" />

                {/* Active Progress Bar */}
                <div
                    className="absolute top-4 left-0 h-1 bg-emerald-500 -z-10 transition-all duration-500"
                    style={{ width: `${(currentIndex / (milestones.length - 1)) * 100}%` }}
                />

                {milestones.map((step, idx) => {
                    const isCompleted = idx <= currentIndex;
                    const isCurrent = idx === currentIndex;

                    return (
                        <div key={step.key} className="flex flex-col items-center">
                            <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors duration-300 z-10 bg-white ${isCompleted
                                    ? 'border-emerald-500 text-emerald-500'
                                    : 'border-slate-300 text-slate-300'
                                    } ${isCurrent ? 'ring-4 ring-emerald-100' : ''}`}
                            >
                                {isCompleted ? <CheckCircle size={18} className="fill-emerald-50" /> : <Circle size={18} />}
                            </div>

                            <div className="mt-4 text-center">
                                <p className={`text-xs font-bold uppercase tracking-wide ${isCompleted ? 'text-navy-900' : 'text-slate-400'}`}>
                                    {step.label}
                                </p>
                                {step.date && (
                                    <p className="text-[10px] text-slate-500 mt-1">
                                        {(() => {
                                            const d = safeDate(step.date);
                                            return d && !isNaN(d) ? format(d, 'dd MMM') : '';
                                        })()}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Shipment Details Grid */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 bg-white p-4 rounded-lg border border-slate-200">
                <div>
                    <span className="text-xs text-slate-400 uppercase">Container Type</span>
                    <p className="text-sm font-medium text-navy-900">{container.containerType || 'FCL'}</p>
                </div>
                <div>
                    <span className="text-xs text-slate-400 uppercase">Mode</span>
                    <p className="text-sm font-medium text-navy-900">{container.mode || 'Sea'}</p>
                </div>
                <div>
                    <span className="text-xs text-slate-400 uppercase">Incoterms</span>
                    <p className="text-sm font-medium text-navy-900">{container.incoterms || '—'}</p>
                </div>
                <div>
                    <span className="text-xs text-slate-400 uppercase">Est. Arrival</span>
                    <p className="text-sm font-medium text-navy-900">
                        {(() => {
                            const d = safeDate(container.eta);
                            return d && !isNaN(d) ? format(d, 'dd MMM yyyy') : 'TBD';
                        })()}
                    </p>
                </div>
            </div>
        </div>
    );
}
